import { useEffect, useRef, useState } from 'react';
import {
  Alert,
  Badge,
  Button,
  Card,
  Checkbox,
  Collapse,
  Divider,
  Group,
  Modal,
  NumberInput,
  Select,
  Stack,
  Table,
  Text,
  TextInput,
  Title,
  Tooltip,
} from '@mantine/core';
import {
  IconChevronDown,
  IconChevronRight,
  IconEdit,
  IconExternalLink,
  IconFileCode,
  IconPlus,
  IconTrash,
  IconTruck,
} from '@tabler/icons-react';
import type {
  AddressBreakdown,
  CarrierDto,
  ContainerUnitOption,
  EtrnDetails,
  ContractPartyDto,
  DeliveryLegCreateInput,
  DeliveryLegDefaults,
  DeliveryLegDto,
  DeliveryLegKind,
  DeliveryLegStatus,
  ExpeditorDocumentDto,
  LegCargo,
  LegParty,
  OrderRouteSegmentDto,
  ShipmentLegDto,
} from '@expeditor/shared';
import type { EtrnProblem, EtrnSection } from '@expeditor/shared';
import {
  contractPartyRoleLabel,
  DELIVERY_LEG_STATUS_LABEL,
  isDomesticRuSegment,
  validateEtrnCompleteness,
  validatePoruchenieCompleteness,
  VAT_RATE_OPTIONS,
  type EdoLegStateDto,
} from '@expeditor/shared';
import { deliveryLegsApi } from '../../shared/api/delivery-legs';
import { visualizePdf } from '../../shared/api/pdf-viz';
import { referenceDataApi } from '../../shared/api/reference-data';
import { CodeSelect } from '../../shared/ui/CodeSelect';
import { carriersApi } from '../../shared/api/shipments';
import { expeditorProfileApi } from '../../shared/api/expeditor-profile';
import { PARTY_TYPE_OPTIONS } from '../../shared/ui/PartyRequisitesFields';
import { routePointKindLabel, transportLabel } from '../../shared/ui/order-labels';
import { AddressBreakdownFields } from '../../shared/ui/AddressBreakdownFields';
import {
  CargoEtrnFields,
  ContractEtrnFields,
  LoadingContractEtrnFields,
  MarksEtrnFields,
  MASS_METHOD_OPTIONS,
  TransportEtrnFields,
} from './EtrnDetailsFields';
import { EdoSubscriberField } from '../../shared/ui/EdoSubscriberField';

/**
 * Откуда выпускается документ:
 * * shipment — плечо «терминал в РФ → получатель» внутри перевозки;
 * * order    — плечо маршрута поручения (напр. ЭТрН на авто-плечо по России).
 */
export type DeliveryLegsSource =
  | {
      kind: 'shipment';
      shipmentId: string;
      /** Привлечённые по договору лица — документы показываем под ними. */
      parties: ContractPartyDto[];
      /** Открыть окно привязки нового лица (кнопка живёт в этом же блоке). */
      onAttachParty?: () => void;
    }
  | { kind: 'order'; orderId: string; segments: OrderRouteSegmentDto[] }
  | { kind: 'shipmentLeg'; shipmentId: string; legs: ShipmentLegDto[] };

interface PartyForm {
  type: string | null;
  name: string;
  inn: string;
  kpp: string;
  address: string;
  phone: string;
  edoSubscriberId: string;
}
const toPartyForm = (p: LegParty): PartyForm => ({
  type: p.type ?? null,
  name: p.name ?? '',
  inn: p.inn ?? '',
  kpp: p.kpp ?? '',
  address: p.address ?? '',
  phone: p.phone ?? '',
  edoSubscriberId: p.edoSubscriberId ?? '',
});
const fromPartyForm = (p: PartyForm): LegParty => ({
  type: p.type,
  name: p.name,
  inn: p.inn || null,
  kpp: p.kpp || null,
  address: p.address || null,
  phone: p.phone || null,
  edoSubscriberId: p.edoSubscriberId || null,
});
const emptyParty: PartyForm = { type: null, name: '', inn: '', kpp: '', address: '', phone: '', edoSubscriberId: '' };

const KIND_LABEL: Record<DeliveryLegKind, string> = {
  ORDER: 'Поручение экспедитору',
  ETRN: 'ЭТрН',
};

/** Подпись стоимости со ставкой НДС: «12 000 RUB · НДС 20%» / «… · без НДС». */
function formatLegPrice(leg: DeliveryLegDto): string | null {
  if (leg.price == null) return null;
  const amount = leg.price.toLocaleString('ru-RU', { minimumFractionDigits: 0, maximumFractionDigits: 2 });
  const vat =
    leg.priceVatRate == null
      ? ''
      : leg.priceVatRate === '-1'
        ? ' · без НДС'
        : ` · НДС ${leg.priceVatRate}%`;
  return `${amount} ${leg.priceCurrency ?? ''}`.trim() + vat;
}

/** Маршрут «пункт отправления → пункт назначения» (пропуская пустые). */
function legRoute(leg: DeliveryLegDto): string {
  return [leg.pickupAddress, leg.deliveryAddress].filter(Boolean).join(' → ');
}

function PartyFields({
  label,
  value,
  onChange,
  withEdo,
}: {
  label?: string;
  value: PartyForm;
  onChange: (p: PartyForm) => void;
  /** Показать поле «Код абонента ЭДО» с кнопкой «Найти» (по ИНН стороны). */
  withEdo?: boolean;
}) {
  return (
    <Stack gap={4}>
      {label ? (
        <Text size="sm" fw={500}>
          {label}
        </Text>
      ) : null}
      <Group grow>
        <TextInput
          label="Наименование / ФИО"
          value={value.name}
          onChange={(e) => onChange({ ...value, name: e.currentTarget.value })}
        />
        <Select
          label="Тип"
          data={PARTY_TYPE_OPTIONS}
          value={value.type}
          onChange={(v) => onChange({ ...value, type: v })}
          clearable
        />
      </Group>
      <Group grow>
        <TextInput
          label="ИНН"
          value={value.inn}
          onChange={(e) => onChange({ ...value, inn: e.currentTarget.value })}
        />
        <TextInput
          label="КПП"
          value={value.kpp}
          onChange={(e) => onChange({ ...value, kpp: e.currentTarget.value })}
        />
        <TextInput
          label="Адрес"
          value={value.address}
          onChange={(e) => onChange({ ...value, address: e.currentTarget.value })}
        />
        <TextInput
          label="Телефон"
          value={value.phone}
          onChange={(e) => onChange({ ...value, phone: e.currentTarget.value })}
        />
      </Group>
      {withEdo && (
        <EdoSubscriberField
          value={value.edoSubscriberId}
          onChange={(v) => onChange({ ...value, edoSubscriberId: v })}
          inn={value.inn}
          description="Код абонента в ЭДО.МИГ24. Подставляется по известным данным; «Найти» ищет по ИНН стороны."
        />
      )}
    </Stack>
  );
}

const DOC_TYPE_LABEL: Record<string, string> = {
  POREXPKLT: 'Поручение (титул клиента)',
  POREXPEXP: 'Поручение (титул экспедитора)',
  POREXPOTZ: 'Отзыв поручения',
  EXPRASP: 'Экспедиторская расписка',
  SKLADRASP: 'Складская расписка',
  ETRN_GO: 'ЭТрН',
  ZAKZVGO: 'ЭЗЗ (заказ-заявка)',
};

/**
 * Документы плеча-поручения (Фаза 2, сценарий мы→исполнители): выпуск поручения
 * исполнителю (исходящий) и приём расписки от него (входящий EXPRASP).
 */
function LegDocumentsCard({
  legId,
  reloadKey,
  onError,
}: {
  legId: string;
  /** Изменение значения перезагружает список (после отзыва поручения и т.п.). */
  reloadKey?: number;
  onError: (m: string) => void;
}) {
  const [docs, setDocs] = useState<ExpeditorDocumentDto[]>([]);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    deliveryLegsApi.documents(legId).then(setDocs).catch(() => {});
  }, [legId, reloadKey]);

  const issueZakzv = async () => {
    setBusy(true);
    try {
      setDocs(await deliveryLegsApi.issueZakzvGo(legId));
    } catch (e) {
      onError(e instanceof Error ? e.message : 'Не удалось выпустить ЭЗЗ');
    } finally {
      setBusy(false);
    }
  };

  const download = async (docId: string) => {
    try {
      const res = await deliveryLegsApi.documentXml(legId, docId);
      const url = URL.createObjectURL(new Blob([res.xml], { type: 'application/xml' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `${res.fileName}.xml`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      onError(e instanceof Error ? e.message : 'Не удалось скачать документ');
    }
  };
  const viewPdf = async (docId: string) => {
    try {
      const res = await deliveryLegsApi.documentXml(legId, docId);
      await visualizePdf(res.fileName, res.xml);
    } catch (e) {
      onError(e instanceof Error ? e.message : 'Не удалось получить PDF');
    }
  };

  return (
    <Card withBorder radius="md" p="sm">
      <Group justify="space-between" mb="xs">
        <Text fw={600}>Документы исполнителю</Text>
        <Button size="xs" variant="light" onClick={issueZakzv} loading={busy}>
          +ЭЗЗ
        </Button>
      </Group>
      {docs.length === 0 ? (
        <Text size="sm" c="dimmed">
          Документов пока нет.
        </Text>
      ) : (
        <Stack gap={4}>
          {docs.map((doc) => (
            <Group key={doc.id} justify="space-between" wrap="nowrap">
              <Text size="sm">
                <Badge
                  size="xs"
                  color={doc.direction === 'INCOMING' ? 'teal' : 'blue'}
                  mr={6}
                >
                  {doc.direction === 'INCOMING' ? 'входящий' : 'исходящий'}
                </Badge>
                {DOC_TYPE_LABEL[doc.type] ?? doc.type}
                {doc.number ? ` № ${doc.number}` : ''} ·{' '}
                {new Date(doc.createdAt).toLocaleDateString('ru-RU')}
              </Text>
              <Group gap={4} wrap="nowrap">
                <Button size="compact-xs" variant="subtle" onClick={() => viewPdf(doc.id)}>
                  PDF
                </Button>
                <Button size="compact-xs" variant="subtle" onClick={() => download(doc.id)}>
                  XML
                </Button>
              </Group>
            </Group>
          ))}
        </Stack>
      )}
    </Card>
  );
}

/**
 * Состояние документа в ЭДО.МИГ24: тянем карточку перевозки/экспедиции и
 * показываем общий статус + пакеты/титулы (ЭТрН — до 7 титулов; поручение —
 * подтверждение экспедитора и расписки). Содержимое (XML) чужих титулов
 * оператор не отдаёт — показываем метаданные и статус.
 */
function EdoStateCard({
  legId,
  onError,
}: {
  legId: string;
  onError: (m: string) => void;
}) {
  const [state, setState] = useState<EdoLegStateDto | null>(null);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    setBusy(true);
    try {
      setState(await deliveryLegsApi.edoState(legId));
    } catch (e) {
      onError(e instanceof Error ? e.message : 'Не удалось получить статус из ЭДО.МИГ24');
    } finally {
      setBusy(false);
    }
  };
  // Подтягиваем при открытии формы.
  useEffect(() => {
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [legId]);

  return (
    <Card withBorder radius="md" p="sm">
      <Group justify="space-between" mb="xs">
        <Text fw={600}>Статус в ЭДО.МИГ24</Text>
        <Button size="xs" variant="light" onClick={load} loading={busy}>
          Обновить из ЭДО
        </Button>
      </Group>
      {state === null ? (
        <Text size="sm" c="dimmed">
          {busy ? 'Загрузка…' : 'Нет данных.'}
        </Text>
      ) : (
        <Stack gap={6}>
          <Text size="sm">
            Общий статус: <b>{state.overallStatus ?? '—'}</b>
          </Text>
          {state.packages.length === 0 ? (
            <Text size="sm" c="dimmed">
              Титулов/пакетов пока нет.
            </Text>
          ) : (
            state.packages.map((p, i) => (
              <Card key={i} withBorder radius="sm" p="xs">
                <Group justify="space-between" wrap="nowrap">
                  <Text size="sm" fw={500}>
                    {p.name ?? p.docKind ?? 'Документ'}
                    {p.number ? ` № ${p.number}` : ''}
                  </Text>
                  <Badge size="xs" color={p.isDraft ? 'gray' : 'teal'}>
                    {p.status ?? (p.isDraft ? 'черновик' : 'готов')}
                  </Badge>
                </Group>
                {p.titles.length > 0 && (
                  <Stack gap={2} mt={4}>
                    {p.titles.map((t, j) => (
                      <Text key={j} size="xs" c="dimmed">
                        {t.docType ?? 'титул'}
                        {t.number ? ` № ${t.number}` : ''} · {t.isDraft ? 'черновик' : 'подписан'}
                      </Text>
                    ))}
                  </Stack>
                )}
              </Card>
            ))
          )}
        </Stack>
      )}
    </Card>
  );
}

/** Индикатор состояния плеча в ЭДО.МИГ24 для списка: статус + флаг обновления. */
function LegEdoBadge({ leg }: { leg: DeliveryLegDto }) {
  if (!leg.edoHasUpdate && !leg.edoStatus) return null;
  return (
    <>
      {leg.edoHasUpdate && (
        <Badge size="xs" color="orange" variant="filled">
          обновление ЭДО
        </Badge>
      )}
      {leg.edoStatus && (
        <Text size="xs" c="dimmed">
          ЭДО: {leg.edoStatus}
        </Text>
      )}
    </>
  );
}

/** Свёрнутая карточка: заголовок + краткая сводка справа, разворачивается. */
function CollapseCard({
  title,
  summary,
  defaultOpen,
  problems,
  children,
}: {
  title: string;
  summary?: string;
  defaultOpen?: boolean;
  /** Список проблем этой секции — подсветить заголовок и раскрыть. */
  problems?: string[];
  children: React.ReactNode;
}) {
  const [open, setOpen] = useState(defaultOpen ?? false);
  const hasProblems = (problems?.length ?? 0) > 0;
  return (
    <Card withBorder padding="xs" style={hasProblems ? { borderColor: 'var(--mantine-color-red-5)' } : undefined}>
      <Group
        justify="space-between"
        wrap="nowrap"
        style={{ cursor: 'pointer' }}
        onClick={() => setOpen((o) => !o)}
      >
        <Text size="sm" c={hasProblems ? 'red' : undefined}>
          <b>{title}</b>
          {summary ? (
            <Text span size="xs" c="dimmed">
              {' — '}
              {summary}
            </Text>
          ) : null}
          {hasProblems ? (
            <Text span size="xs" c="red">
              {' '}· заполните {problems!.length}
            </Text>
          ) : null}
        </Text>
        {open ? <IconChevronDown size={16} /> : <IconChevronRight size={16} />}
      </Group>
      <Collapse in={open || hasProblems}>
        <div style={{ marginTop: 8 }}>
          {hasProblems && (
            <Text size="xs" c="red" mb={4}>
              {problems!.join('; ')}
            </Text>
          )}
          {children}
        </div>
      </Collapse>
    </Card>
  );
}

/** Сторона документа в свёрнутом виде: «Ярлык — Наименование», разворачивается. */
function CollapsibleParty({
  label,
  value,
  onChange,
  withEdo,
  problems,
  children,
}: {
  label: string;
  value: PartyForm;
  onChange: (p: PartyForm) => void;
  withEdo?: boolean;
  problems?: string[];
  children?: React.ReactNode;
}) {
  const [open, setOpen] = useState(false);
  const hasProblems = (problems?.length ?? 0) > 0;
  return (
    <Card withBorder padding="xs" style={hasProblems ? { borderColor: 'var(--mantine-color-red-5)' } : undefined}>
      <Group
        justify="space-between"
        wrap="nowrap"
        style={{ cursor: 'pointer' }}
        onClick={() => setOpen((o) => !o)}
      >
        <Text size="sm" c={hasProblems ? 'red' : undefined}>
          <b>{label}</b>
          {value.name ? ` — ${value.name}` : ' — не указан'}
          {withEdo && value.edoSubscriberId ? (
            <Text span size="xs" c="dimmed">
              {' '}
              · ЭДО: {value.edoSubscriberId}
            </Text>
          ) : null}
          {hasProblems ? (
            <Text span size="xs" c="red">
              {' '}· заполните {problems!.length}
            </Text>
          ) : null}
        </Text>
        {open ? <IconChevronDown size={16} /> : <IconChevronRight size={16} />}
      </Group>
      <Collapse in={open || hasProblems}>
        <div style={{ marginTop: 8 }}>
          {hasProblems && (
            <Text size="xs" c="red" mb={4}>
              {problems!.join('; ')}
            </Text>
          )}
          <PartyFields value={value} onChange={onChange} withEdo={withEdo} />
          {children}
        </div>
      </Collapse>
    </Card>
  );
}

export function DeliveryLegsSection({ source }: { source: DeliveryLegsSource }) {
  const [legs, setLegs] = useState<DeliveryLegDto[]>([]);
  const [carriers, setCarriers] = useState<CarrierDto[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  // форма
  const [formKind, setFormKind] = useState<DeliveryLegKind | null>(null);
  // Что выпускаем из формы ЭТрН: саму ЭТрН или ЭЗЗ (заказ-заявка) — общая модель данных.
  const [docKind, setDocKind] = useState<'ETRN' | 'ZAKZVGO'>('ETRN');
  const [editingId, setEditingId] = useState<string | null>(null);
  /** Плечо поручения, на которое выпускается документ (для source=order). */
  const [segmentId, setSegmentId] = useState<string | null>(null);
  // Плечо перевозки, на которое выпускается документ (источник shipmentLeg).
  const [shipmentLegId, setShipmentLegId] = useState<string | null>(null);
  const [legModes, setLegModes] = useState<string[]>(['AUTO']);
  /** Выбранное привлекаемый исполнитель — исполнитель по документу. */
  const [performerId, setPerformerId] = useState('');
  const [dateFrom, setDateFrom] = useState<string | null>(null);
  const [dateTo, setDateTo] = useState<string | null>(null);
  const [customer, setCustomer] = useState<PartyForm>(emptyParty);
  const [expeditor, setExpeditor] = useState<PartyForm>(emptyParty);
  const [shipper, setShipper] = useState<PartyForm>(emptyParty);
  const [consignee, setConsignee] = useState<PartyForm>(emptyParty);
  const [pickupAddress, setPickupAddress] = useState('');
  const [deliveryAddress, setDeliveryAddress] = useState('');
  // Разбивки адресов (АдрРФ) — для ЭТрН; подтягиваются по тексту адреса.
  const [pickupParts, setPickupParts] = useState<AddressBreakdown>({});
  const [deliveryParts, setDeliveryParts] = useState<AddressBreakdown>({});
  const [cargoOptions, setCargoOptions] = useState<LegCargo[]>([]);
  const [cargo, setCargo] = useState<LegCargo>({ name: '', isDangerous: false, containers: [] });
  // Режим «ЭТрН на один контейнер» (настройка экспедитора, выставляет админ).
  const [oneContainerPerDoc, setOneContainerPerDoc] = useState(false);
  const [containerUnits, setContainerUnits] = useState<ContainerUnitOption[]>([]);
  const [selectedContainerId, setSelectedContainerId] = useState<string | null>(null);
  // Выбор контейнера перед открытием формы ЭТрН (режим «один контейнер»).
  const [containerPick, setContainerPick] = useState<{
    defaults: DeliveryLegDefaults;
    segment?: OrderRouteSegmentDto;
    performer: CarrierDto | null;
  } | null>(null);
  // ETRN
  const [carrierId, setCarrierId] = useState('');
  const [carrierManual, setCarrierManual] = useState<PartyForm>(emptyParty);
  const [driverName, setDriverName] = useState('');
  const [driverPhone, setDriverPhone] = useState('');
  const [vehicleRegNo, setVehicleRegNo] = useState('');
  const [vehicleType, setVehicleType] = useState('');
  const [etrnDetails, setEtrnDetails] = useState<EtrnDetails>({});
  const [shipperIsExpeditor, setShipperIsExpeditor] = useState(true);
  // Номер документа: пусто = автогенерация (при создании); при правке — редактируемый.
  const [numberValue, setNumberValue] = useState('');
  // Стоимость перевозки привлекаемому исполнителю (менеджер) + ставка НДС + валюта + статус.
  const [price, setPrice] = useState<number | ''>('');
  const [priceVatRate, setPriceVatRate] = useState<string | null>('20');
  const [priceCurrency, setPriceCurrency] = useState('RUB');
  const [status, setStatus] = useState<DeliveryLegStatus>('CREATED');
  // Проблемы проверки заполнения (по XSD) — секции с недостающими полями.
  const [checkProblems, setCheckProblems] = useState<EtrnProblem[] | null>(null);
  // Отслеживание несохранённых изменений: слепок формы на момент открытия/сохранения.
  const savedSnapshot = useRef('');
  // Id последнего сохранённого документа (нужен сразу после создания для подписания).
  const savedLegIdRef = useRef<string | null>(null);
  const [closeAsk, setCloseAsk] = useState(false);
  // Встроенная страница подписания ЭДО.МИГ24 (visualizationUrl в модалке-iframe).
  const [signPageUrl, setSignPageUrl] = useState<string | null>(null);
  // Ссылка на перевозку в веб-кабинете ЭДО.МИГ24 (после создания титула 1) —
  // для кнопки «Открыть перевозку в ЭДО.МИГ24».
  const [edoSignPageUrl, setEdoSignPageUrl] = useState<string | null>(null);
  // Результат валидации XML от ЭДО (ошибки/предупреждения после «Подписать»).
  const [edoValidation, setEdoValidation] = useState<{
    errors: string[];
    warnings: string[];
  } | null>(null);
  // Strict отклонил документ по ошибкам — показываем кнопку «Всё равно создать».
  const [edoBlocked, setEdoBlocked] = useState(false);
  // Тик пересъёма базового слепка (после асинхронной подгрузки разбивок адресов).
  const [baselineTick, setBaselineTick] = useState(0);
  // Тик перезагрузки списка «Документы исполнителю» (после отзыва поручения).
  const [docsReloadKey, setDocsReloadKey] = useState(0);

  const sourceKey = source.kind === 'order' ? source.orderId : source.shipmentId;

  const reload = async () => {
    try {
      setLegs(
        source.kind === 'order'
          ? await deliveryLegsApi.listForOrder(source.orderId)
          : await deliveryLegsApi.list(source.shipmentId),
      );
      setError(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Не удалось загрузить документы');
    }
  };

  useEffect(() => {
    void reload();
    carriersApi.list().then(setCarriers).catch(() => {});
    expeditorProfileApi
      .get()
      .then((p) => setOneContainerPerDoc(p.etrnOneContainerPerDoc))
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sourceKey]);

  /** Номер ЭТрН из номера контейнера — только цифры (MSCU1234567 → 1234567). */
  const containerDigits = (n: string | null): string => (n ?? '').replace(/\D/g, '');

  /** Подставить контейнер в форму ЭТрН: его груз и номер ЭТрН из номера контейнера. */
  const pickContainer = (u: ContainerUnitOption) => {
    setSelectedContainerId(u.id);
    setCargo({ ...u.cargo });
    setNumberValue(containerDigits(u.number));
  };

  const setCargoFrom = (c: LegCargo) => setCargo({ ...c });

  /** Есть ли в разбивке заполненные поля. */
  const hasAnyPart = (p: AddressBreakdown) =>
    Object.values(p).some((v) => v != null && String(v).trim() !== '');

  /** Подтянуть сохранённые разбивки по тексту адресов (или очистить). */
  const loadBreakdowns = async (pickup: string, delivery: string) => {
    const [p, d] = await Promise.all([
      pickup.trim() ? deliveryLegsApi.addressBreakdown(pickup).catch(() => null) : null,
      delivery.trim() ? deliveryLegsApi.addressBreakdown(delivery).catch(() => null) : null,
    ]);
    setPickupParts(p ?? {});
    setDeliveryParts(d ?? {});
  };

  /**
   * Новый документ: по перевозке (для конкретного привлечённого лица) или по
   * плечу маршрута поручения.
   */
  const openCreate = async (
    kind: DeliveryLegKind,
    segment?: OrderRouteSegmentDto,
    performer?: CarrierDto | null,
    leg?: ShipmentLegDto,
    asZakzv = false,
  ) => {
    setError(null);
    // ЭЗЗ (заказ-заявка) использует ту же форму и данные, что и ЭТрН (kind=ETRN),
    // но из формы выпускается документ ON_ZAKZVGO, а не ЭТрН.
    setDocKind(asZakzv ? 'ZAKZVGO' : 'ETRN');
    // Исполнитель плеча — сторона документа: без него ЭТрН/поручение не выпускаем.
    if (leg && !leg.carrierId) {
      setError(
        'У плеча не указан исполнитель — укажите его в «Плечах перевозки» (кнопка «Правка»). Исполнитель является стороной документа.',
      );
      return;
    }
    // Плечо перевозки, на которое выпускается документ (источник shipmentLeg).
    setShipmentLegId(leg?.id ?? null);
    // Исполнитель плеча — привлекаемый исполнитель из его карточки.
    const effPerformer =
      performer ??
      (leg?.carrierId ? carriers.find((c) => c.id === leg.carrierId) ?? null : null);
    try {
      const d =
        source.kind === 'order' && segment
          ? await deliveryLegsApi.segmentDefaults(source.orderId, segment.id)
          : source.kind === 'shipmentLeg' && leg
            ? await deliveryLegsApi.legDefaults(source.shipmentId, leg.id)
            : await deliveryLegsApi.defaults(
                source.kind === 'shipment' ? source.shipmentId : '',
              );
      // Режим «один контейнер на ЭТрН»: при нескольких контейнерах поручения
      // сначала предлагаем выбрать контейнер, и только потом открываем форму.
      if (kind === 'ETRN' && oneContainerPerDoc && d.containerUnits.length > 1) {
        setContainerPick({ defaults: d, segment, performer: effPerformer });
        return;
      }
      const preselectId =
        kind === 'ETRN' && oneContainerPerDoc && d.containerUnits.length === 1
          ? d.containerUnits[0].id
          : null;
      applyDefaults(kind, d, segment, effPerformer, preselectId);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Не удалось загрузить данные базового поручения');
    }
  };

  /** Заполнить и открыть форму по загруженным дефолтам (+ опц. выбранный контейнер). */
  const applyDefaults = (
    kind: DeliveryLegKind,
    d: DeliveryLegDefaults,
    segment?: OrderRouteSegmentDto,
    performer?: CarrierDto | null,
    preselectContainerId?: string | null,
  ) => {
    setEditingId(null);
    setEdoSignPageUrl(null);
    setSegmentId(segment?.id ?? null);
    {
      const our = toPartyForm(d.expeditorParty);
      if (performer) {
        // Документ привлечённому лицу: заказчик — мы, исполнитель — это лицо.
        setPerformerId(performer.id);
        setCustomer(our);
        setExpeditor({
          type: performer.type ?? null,
          // Для ЭТрН нужно полное наименование перевозчика, не краткое.
          name: performer.fullName || performer.shortName || '',
          inn: performer.inn ?? '',
          kpp: '',
          address: '',
          phone: '',
          // Код абонента ЭДО перевозчика — сразу из его карточки, если задан.
          edoSubscriberId: performer.edoSubscriberId ?? '',
        });
      } else {
        setPerformerId('');
        setCustomer(toPartyForm(d.customerParty));
        setExpeditor(our);
      }
      // Грузоотправитель: для документов привлекаемому исполнителю по умолчанию — наш
      // экспедитор (он же заказчик); если из поручения пришёл конкретный —
      // берём его.
      const shipperDefault = toPartyForm(d.shipperParty);
      setShipper(!isOrderSource && !shipperDefault.name ? our : shipperDefault);
      setConsignee(toPartyForm(d.consigneeParty));
      setPickupAddress(d.pickupAddress ?? '');
      setDeliveryAddress(d.deliveryAddress);
      void loadBreakdowns(d.pickupAddress ?? '', d.deliveryAddress).then(() =>
        setBaselineTick((t) => t + 1),
      );
      setLegModes(d.transportModes?.length ? d.transportModes : ['AUTO']);
      setDateFrom(d.dateFrom ?? null);
      setDateTo(d.dateTo ?? null);
      setCargoOptions(d.cargoOptions);
      setContainerUnits(d.containerUnits);
      setSelectedContainerId(null);
      setCarrierId('');
      setCarrierManual(emptyParty);
      setDriverName('');
      setDriverPhone('');
      setVehicleRegNo('');
      setVehicleType('');
      // Предзаполняем договор ТЭО (ДогУслПер, блок 1а ЭТрН) из договора перевозки.
      setEtrnDetails(d.etrnContract ? { contract: d.etrnContract } : {});
      setShipperIsExpeditor(true);
      setNumberValue('');
      // Режим «один контейнер на ЭТрН»: контейнер уже выбран до открытия формы —
      // подставляем его груз и номер ЭТрН. Иначе — груз из первой опции поручения.
      const chosen =
        preselectContainerId != null
          ? d.containerUnits.find((u) => u.id === preselectContainerId)
          : undefined;
      if (chosen) {
        pickContainer(chosen);
      } else {
        setCargo(d.cargoOptions[0] ?? { name: '', isDangerous: false, containers: [] });
      }
      setPrice('');
      // Ставка НДС по умолчанию — из карточки привлекаемого исполнителя (performer),
      // если задана; иначе 20%.
      setPriceVatRate(performer?.vatRate ?? '20');
      setPriceCurrency('RUB');
      setStatus('CREATED');
      setCheckProblems(null);
      setEdoValidation(null);
      setFormKind(kind);
    }
  };

  /**
   * Выбор привлекаемого исполнителя: по такому документу заказчиком выступает наш
   * экспедитор, а исполнителем («экспедитором» по формату ФНС) — это лицо.
   * Обе стороны остаются редактируемыми вручную.
   */
  const openEdit = (leg: DeliveryLegDto) => {
    setError(null);
    setEditingId(leg.id);
    setSegmentId(leg.orderSegmentId);
    setShipmentLegId(leg.shipmentLegId);
    setPerformerId(leg.performerCarrierId ?? '');
    setLegModes(leg.transportModes.length ? leg.transportModes : ['AUTO']);
    setDateFrom(leg.dateFrom);
    setDateTo(leg.dateTo);
    setCustomer(toPartyForm(leg.customerParty));
    setExpeditor(toPartyForm(leg.expeditorParty));
    setShipper(toPartyForm(leg.shipperParty));
    setConsignee(toPartyForm(leg.consigneeParty));
    setPickupAddress(leg.pickupAddress);
    setDeliveryAddress(leg.deliveryAddress);
    void loadBreakdowns(leg.pickupAddress, leg.deliveryAddress).then(() =>
      setBaselineTick((t) => t + 1),
    );
    setCargoOptions([leg.cargo]);
    setCargo(leg.cargo);
    // При редактировании выбор контейнера не форсируем — документ уже создан.
    setContainerUnits([]);
    setSelectedContainerId(null);
    setCarrierId(leg.carrierId ?? '');
    setCarrierManual(leg.carrierParty ? toPartyForm(leg.carrierParty) : emptyParty);
    setDriverName(leg.driverName ?? '');
    setDriverPhone(leg.driverPhone ?? '');
    setVehicleRegNo(leg.vehicleRegNo ?? '');
    setVehicleType(leg.vehicleType ?? '');
    setEtrnDetails(leg.etrnDetails ?? {});
    setShipperIsExpeditor(leg.shipperIsExpeditor ?? true);
    setNumberValue(leg.number);
    setPrice(leg.price ?? '');
    setPriceVatRate(leg.priceVatRate ?? '-1');
    setPriceCurrency(leg.priceCurrency ?? 'RUB');
    setStatus(leg.status);
    setCheckProblems(null);
    setEdoValidation(null);
    setDocKind('ETRN');
    setEdoSignPageUrl(leg.edoSignUrl ?? null);
    setFormKind(leg.kind);
  };

  /** Проверить заполнение ЭТрН по XSD и подсветить недостающее. */
  /** Слепок всех полей формы — по нему определяем несохранённые изменения. */
  const currentSnapshot = (): string =>
    JSON.stringify({
      performerId,
      legModes,
      dateFrom,
      dateTo,
      customer,
      expeditor,
      shipper,
      consignee,
      pickupAddress,
      deliveryAddress,
      pickupParts,
      deliveryParts,
      cargo,
      carrierId,
      carrierManual,
      driverName,
      driverPhone,
      vehicleRegNo,
      vehicleType,
      etrnDetails,
      shipperIsExpeditor,
      numberValue,
      price,
      priceVatRate,
      priceCurrency,
      status,
    });

  // Базовый слепок снимаем после подгрузки разбивок адресов (baselineTick),
  // чтобы асинхронная загрузка не считалась «изменением».
  useEffect(() => {
    if (formKind) savedSnapshot.current = currentSnapshot();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [baselineTick]);

  const isDirty = (): boolean =>
    formKind !== null && currentSnapshot() !== savedSnapshot.current;

  /** Закрытие формы: при несохранённых изменениях — спросить. */
  const attemptClose = () => {
    if (isDirty()) setCloseAsk(true);
    else setFormKind(null);
  };

  const runCheck = () => {
    const input = {
      customer: fromPartyForm(customer),
      carrier: fromPartyForm(expeditor),
      shipper: fromPartyForm(shipper),
      consignee: fromPartyForm(consignee),
      pickupAddress,
      deliveryAddress,
      pickupAddressParts: pickupParts,
      deliveryAddressParts: deliveryParts,
      cargo,
      driverName,
      driverPhone,
      etrn: etrnDetails,
    };
    // Поручение (POREXPKLT) и ЭТрН (ON_TRNACLGROT) — разные XSD, свои наборы
    // обязательных полей.
    const problems =
      formKind === 'ORDER'
        ? validatePoruchenieCompleteness(input)
        : validateEtrnCompleteness(input);
    setCheckProblems(problems);
    return problems;
  };
  /** Проблемы конкретной секции (для подсветки карточки). */
  const secProblems = (section: EtrnSection): string[] =>
    (checkProblems ?? []).filter((p) => p.section === section).map((p) => p.message);

  /**
   * Подписание ЭТрН в ЭДО.МИГ24. Проверяем заполнение → сохраняем → создаём
   * перевозку через внешний API ЭДО (титул 1) → открываем страницу МИГ24
   * встроенно (iframe), где выбирается способ подписания и подписывается Т1.
   * Статусы обработки подтягивает фоновый воркер (см. бэкенд).
   */
  // Создание перевозки в ЭДО. force=false — режим Strict (при ошибках не
  // создаём, показываем их и предлагаем создать всё равно); force=true —
  // Deferred (создаём несмотря на ошибки, проверка отложенная).
  const doSignEdo = async (legId: string, force: boolean) => {
    setBusy(true);
    setEdoValidation(null);
    setEdoBlocked(false);
    try {
      const { signUrl, validation, created } = await deliveryLegsApi.signEdo(legId, force);
      await reload();
      const hasVal = validation && (validation.errors.length > 0 || validation.warnings.length > 0);
      setEdoValidation(hasVal ? { errors: validation.errors, warnings: validation.warnings } : null);
      if (created && signUrl) {
        // Перевозка создана. Сохраняем ссылку для кнопки «Открыть перевозку в
        // ЭДО.МИГ24» и сразу открываем вкладку; при блокировке попапа — модалка.
        setEdoSignPageUrl(signUrl);
        const w = window.open(signUrl, '_blank', 'noopener,noreferrer');
        if (!w) setSignPageUrl(signUrl);
      } else if (created && !signUrl) {
        setError('Перевозка создана в ЭДО.МИГ24. Откройте её в ЭДО и подпишите.');
      } else if (!created) {
        // Strict отклонил документ. Если есть ошибки — предлагаем создать всё
        // равно (Deferred); иначе показываем общую ошибку внутри модалки.
        if (validation && validation.errors.length > 0) {
          setEdoBlocked(true);
        } else {
          setEdoValidation({ errors: ['Не удалось создать документ в ЭДО.МИГ24'], warnings: [] });
        }
      }
    } catch (e) {
      // Ошибку показываем ВНУТРИ модалки (через edoValidation) — общий error-алерт
      // рендерится в секции позади модалки и не виден при открытой форме.
      const msg = e instanceof Error ? e.message : 'Не удалось создать документ в ЭДО.МИГ24';
      setEdoValidation({ errors: [msg], warnings: [] });
    } finally {
      setBusy(false);
    }
  };

  const onSignEdo = async () => {
    // Для ЭТрН перед подписанием прогоняем проверку заполнения по XSD; для
    // поручения (ORDER) строгую проверку делает сам ЭДО при создании экспедиции.
    if (formKind === 'ETRN') {
      const problems = runCheck();
      if (problems.length > 0) {
        setError(`ЭТрН заполнена не полностью (${problems.length}). Исправьте отмеченные поля.`);
        return;
      }
    }
    setError(null);
    // Документ должен быть сохранён (нужен id и актуальные данные).
    if (!editingId || isDirty()) {
      const ok = await onSave();
      if (!ok) return;
    }
    const legId = editingId ?? savedLegIdRef.current;
    if (!legId) return;
    await doSignEdo(legId, false);
  };

  // Выпустить ЭЗЗ из формы: сохраняем плечо (данные ЭТрН) и выпускаем документ
  // ON_ZAKZVGO. Строгую проверку ЭТрН не гоняем — у ЭЗЗ свой (меньший) набор полей.
  const onIssueZakzvForm = async () => {
    setError(null);
    if (!editingId || isDirty()) {
      const ok = await onSave();
      if (!ok) return;
    }
    const legId = editingId ?? savedLegIdRef.current;
    if (!legId) return;
    setBusy(true);
    try {
      await deliveryLegsApi.issueZakzvGo(legId);
      setError(null);
      setFormKind(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Не удалось выпустить ЭЗЗ');
    } finally {
      setBusy(false);
    }
  };

  // Отозвать поручение (POREXPOTZ) — доступно для сохранённого плеча-поручения.
  const onRevokePoruchenie = async () => {
    if (!editingId) return;
    setBusy(true);
    setError(null);
    try {
      await deliveryLegsApi.revokePoruchenie(editingId);
      setDocsReloadKey((k) => k + 1);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Не удалось отозвать поручение');
    } finally {
      setBusy(false);
    }
  };

  // «Всё равно создать с ошибками» — повторный вызов в режиме Deferred.
  const onSignEdoForce = async () => {
    const legId = editingId ?? savedLegIdRef.current;
    if (!legId) return;
    await doSignEdo(legId, true);
  };

  const onSave = async (): Promise<boolean> => {
    if (!formKind) return false;
    // Проверяем до отправки: сервер требует оба пункта маршрута, и без этой
    // проверки пользователь получал только «400» без объяснения.
    const needContainer =
      !editingId &&
      formKind === 'ETRN' &&
      oneContainerPerDoc &&
      containerUnits.length > 0 &&
      !selectedContainerId;
    const problems = [
      !pickupAddress.trim() && 'пункт отправления',
      !deliveryAddress.trim() && 'пункт доставки',
      !expeditor.name.trim() && (isOrderSource ? 'экспедитор' : 'исполнитель'),
      needContainer && 'контейнер для ЭТрН',
    ].filter(Boolean) as string[];
    if (problems.length > 0) {
      setError(`Заполните: ${problems.join(', ')}.`);
      return false;
    }
    setBusy(true);
    setError(null);
    const isEtrn = formKind === 'ETRN';
    const payload: DeliveryLegCreateInput = {
      kind: formKind,
      ...(numberValue.trim() && { number: numberValue.trim() }),
      ...(segmentId && { orderSegmentId: segmentId }),
      ...(shipmentLegId && { shipmentLegId }),
      performerCarrierId: performerId || null,
      customerParty: fromPartyForm(customer),
      expeditorParty: fromPartyForm(expeditor),
      shipperParty: fromPartyForm(shipper),
      consigneeParty: fromPartyForm(consignee),
      pickupAddress,
      deliveryAddress,
      transportModes: legModes,
      cargo,
      ...(dateFrom && { dateFrom }),
      ...(dateTo && { dateTo }),
      ...(hasAnyPart(pickupParts) && { pickupAddressParts: pickupParts }),
      ...(hasAnyPart(deliveryParts) && { deliveryAddressParts: deliveryParts }),
      ...(price !== '' && { price: Number(price) }),
      priceVatRate: priceVatRate ?? '-1',
      priceCurrency,
      ...(isEtrn && {
        carrierId: carrierId || null,
        carrier: !carrierId && carrierManual.name ? fromPartyForm(carrierManual) : null,
        driverName: driverName || undefined,
        driverPhone: driverPhone || undefined,
        vehicleRegNo: vehicleRegNo || undefined,
        vehicleType: vehicleType || undefined,
        etrnDetails,
        shipperIsExpeditor,
      }),
    };
    try {
      if (editingId) {
        const { kind: _k, orderSegmentId: _s, shipmentLegId: _l, ...patch } = payload;
        void _k;
        void _s;
        void _l;
        await deliveryLegsApi.patch(editingId, { ...patch, status });
        savedLegIdRef.current = editingId;
      } else if (source.kind === 'shipment' || source.kind === 'shipmentLeg') {
        const created = await deliveryLegsApi.create(source.shipmentId, payload);
        // После создания форма остаётся открытой уже в режиме правки.
        setEditingId(created.id);
        setNumberValue(created.number);
        savedLegIdRef.current = created.id;
      } else {
        const created = await deliveryLegsApi.createForOrder(source.orderId, payload);
        setEditingId(created.id);
        setNumberValue(created.number);
        savedLegIdRef.current = created.id;
      }
      await reload();
      // Сохранение прошло — пересъём базового слепка (после того как
      // применятся изменения editingId/номера) сбрасывает «грязность».
      setBaselineTick((t) => t + 1);
      return true;
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Не удалось сохранить документ');
      return false;
    } finally {
      setBusy(false);
    }
  };

  const onXml = async (leg: DeliveryLegDto) => {
    // Для ЭТрН перед выгрузкой предупреждаем о незаполненных обязательных полях
    // (по XSD) и об отсутствии кодов абонента ЭДО, но выгрузку не блокируем.
    if (leg.kind === 'ETRN') {
      const problems = validateEtrnCompleteness({
        customer: leg.customerParty,
        carrier: leg.expeditorParty,
        shipper: leg.shipperParty,
        consignee: leg.consigneeParty,
        pickupAddress: leg.pickupAddress,
        deliveryAddress: leg.deliveryAddress,
        cargo: leg.cargo,
        driverName: leg.driverName ?? '',
        driverPhone: leg.driverPhone ?? '',
        etrn: leg.etrnDetails ?? {},
      });
      if (problems.length > 0) {
        const list = problems.map((p) => `• ${p.message}`).join('\n');
        const ok = confirm(
          `ЭТрН заполнена не полностью (${problems.length}). XML будет невалиден по XSD:\n\n${list}\n\nВсё равно сформировать XML?`,
        );
        if (!ok) return;
      }
    }
    setBusy(true);
    try {
      const { fileName, xml } = await deliveryLegsApi.xml(leg.id);
      const url = URL.createObjectURL(new Blob([xml], { type: 'application/xml' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `${fileName}.xml`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Не удалось сформировать XML');
    } finally {
      setBusy(false);
    }
  };

  // PDF-визуализация документа плеча (ЭТрН/поручение) через сервис xmledo2pdf.
  const onLegPdf = async (leg: DeliveryLegDto) => {
    setBusy(true);
    try {
      const { fileName, xml } = await deliveryLegsApi.xml(leg.id);
      await visualizePdf(fileName, xml);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Не удалось получить PDF');
    } finally {
      setBusy(false);
    }
  };

  const onDelete = async (leg: DeliveryLegDto) => {
    if (!confirm(`Удалить документ ${leg.number}?`)) return;
    setBusy(true);
    try {
      await deliveryLegsApi.remove(leg.id);
      await reload();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Не удалось удалить');
    } finally {
      setBusy(false);
    }
  };

  const cargoOptionData = cargoOptions.map((c, i) => ({
    value: String(i),
    label: `${c.name || 'Груз'}${c.containers.length ? ` · ${c.containers.join(', ')}` : ''}`,
  }));

  const isOrderSource = source.kind === 'order';
  /** Документы без исполнителя: «прочие» — только для перевозки (привл. лица). */
  const otherLegs = source.kind === 'shipment' ? legs.filter((l) => !l.performerCarrierId) : legs;

  return (
    <Card withBorder>
      <Group justify="space-between" mb="sm">
        <div>
          <Title order={4}>
            {source.kind === 'order'
              ? 'Документы на плечи маршрута'
              : source.kind === 'shipmentLeg'
                ? 'Документы по плечам перевозки'
                : 'Привлекаемые исполнители и поручения им'}
          </Title>
          {(source.kind === 'order' || source.kind === 'shipmentLeg') && (
            <Text size="xs" c="dimmed">
              Поручения экспедитору и ЭТрН по плечам (исполнитель берётся из плеча)
            </Text>
          )}
        </div>
        {source.kind === 'shipment' && source.onAttachParty && (
          <Button
            size="xs"
            leftSection={<IconTruck size={14} />}
            onClick={source.onAttachParty}
          >
            Привязать исполнителя
          </Button>
        )}
      </Group>

      {error && (
        <Alert color="red" mb="sm" withCloseButton onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {source.kind === 'order' &&
        (source.segments.length === 0 ? (
          <Alert color="gray" mb="sm">
            Маршрут не уточнён по плечам — уточните его в поручении, и документ можно будет
            выпустить прямо на плечо.
          </Alert>
        ) : (
          <Table mb="md" withTableBorder>
            <Table.Thead>
              <Table.Tr>
                <Table.Th style={{ width: 40 }}>#</Table.Th>
                <Table.Th>Плечо</Table.Th>
                <Table.Th style={{ width: 260 }} />
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {source.segments.map((s) => {
                // ЭТрН — только на авто-плечо целиком по России (внутрироссийская
                // автоперевозка; для остальных плеч она неприменима).
                const etrnAvailable = s.transportMode === 'AUTO' && isDomesticRuSegment(s);
                return (
                  <Table.Tr key={s.id}>
                    <Table.Td>{s.sequence}</Table.Td>
                    <Table.Td>
                      <Text size="sm">
                        {s.fromName || routePointKindLabel(s.fromKind)} →{' '}
                        {s.toName || routePointKindLabel(s.toKind)}
                      </Text>
                      <Text size="xs" c="dimmed">
                        {transportLabel(s.transportMode)}
                        {s.documentNumbers.length > 0 && ` · документы: ${s.documentNumbers.join(', ')}`}
                      </Text>
                    </Table.Td>
                    <Table.Td>
                      <Group gap={4} wrap="nowrap" justify="flex-end">
                        <Button
                          size="xs"
                          variant="default"
                          leftSection={<IconPlus size={14} />}
                          onClick={() => openCreate('ORDER', s)}
                        >
                          Поручение
                        </Button>
                        <Tooltip
                          label="ЭТрН оформляется на авто-плечо внутри России — укажите вид транспорта «авто» и страну обеих точек"
                          disabled={etrnAvailable}
                          multiline
                          w={280}
                        >
                          <div>
                            <Button
                              size="xs"
                              variant="default"
                              disabled={!etrnAvailable}
                              leftSection={<IconPlus size={14} />}
                              onClick={() => openCreate('ETRN', s)}
                            >
                              ЭТрН
                            </Button>
                          </div>
                        </Tooltip>
                        <Button
                          size="xs"
                          variant="default"
                          color="grape"
                          leftSection={<IconPlus size={14} />}
                          onClick={() => openCreate('ETRN', s, undefined, undefined, true)}
                        >
                          ЭЗЗ
                        </Button>
                      </Group>
                    </Table.Td>
                  </Table.Tr>
                );
              })}
            </Table.Tbody>
          </Table>
        ))}

      {source.kind === 'shipmentLeg' &&
        (source.legs.length === 0 ? (
          <Alert color="gray" mb="sm">
            Плечи перевозки ещё не заданы — добавьте их в блоке «Плечи перевозки» выше,
            и на каждое можно будет выпустить ЭТрН/поручение.
          </Alert>
        ) : (
          <Table mb="md" withTableBorder>
            <Table.Thead>
              <Table.Tr>
                <Table.Th style={{ width: 40 }}>#</Table.Th>
                <Table.Th>Плечо</Table.Th>
                <Table.Th style={{ width: 260 }} />
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {source.legs.map((l) => {
                const etrnAvailable = l.transportMode === 'AUTO' && isDomesticRuSegment(l);
                return (
                  <Table.Tr key={l.id}>
                    <Table.Td>{l.sequence}</Table.Td>
                    <Table.Td>
                      <Text size="sm">
                        {l.fromName || routePointKindLabel(l.fromKind)} →{' '}
                        {l.toName || routePointKindLabel(l.toKind)}
                      </Text>
                      <Text size="xs" c="dimmed">
                        {transportLabel(l.transportMode)}
                        {l.carrierName ? (
                          ` · исполнитель: ${l.carrierName}`
                        ) : (
                          <Text span size="xs" c="orange">
                            {' '}· исполнитель не указан
                          </Text>
                        )}
                        {l.documentNumbers.length > 0 &&
                          ` · документы: ${l.documentNumbers.join(', ')}`}
                      </Text>
                    </Table.Td>
                    <Table.Td>
                      <Group gap={4} wrap="nowrap" justify="flex-end">
                        <Button
                          size="xs"
                          variant="default"
                          leftSection={<IconPlus size={14} />}
                          onClick={() => openCreate('ORDER', undefined, undefined, l)}
                        >
                          Поручение
                        </Button>
                        <Tooltip
                          label="ЭТрН оформляется на авто-плечо внутри России — вид транспорта «авто» и страна обеих точек РФ"
                          disabled={etrnAvailable}
                          multiline
                          w={280}
                        >
                          <div>
                            <Button
                              size="xs"
                              variant="default"
                              disabled={!etrnAvailable}
                              leftSection={<IconPlus size={14} />}
                              onClick={() => openCreate('ETRN', undefined, undefined, l)}
                            >
                              ЭТрН
                            </Button>
                          </div>
                        </Tooltip>
                        <Button
                          size="xs"
                          variant="default"
                          color="grape"
                          leftSection={<IconPlus size={14} />}
                          onClick={() => openCreate('ETRN', undefined, undefined, l, true)}
                        >
                          ЭЗЗ
                        </Button>
                      </Group>
                    </Table.Td>
                  </Table.Tr>
                );
              })}
            </Table.Tbody>
          </Table>
        ))}

      {source.kind === 'shipment' && (
        <Stack gap="sm" mb="md">
          {source.parties.length === 0 && (
            <Text c="dimmed" size="sm">
              Привлекаемые исполнители ещё не привязаны — привяжите исполнителя, чтобы выдать ему поручение.
            </Text>
          )}
          {source.parties.map((party) => {
            const carrier = party.carrier ?? null;
            // Документы этого лица — по исполнителю; у старых документов
            // исполнитель не проставлен, они уходят в «Прочие документы».
            const partyLegs = carrier ? legs.filter((l) => l.performerCarrierId === carrier.id) : [];
            return (
              <Card key={party.id} withBorder padding="sm">
                <Group justify="space-between" wrap="nowrap">
                  <Group gap="xs" wrap="nowrap">
                    <Badge>{contractPartyRoleLabel(party.role)}</Badge>
                    <div>
                      <Text size="sm" fw={500}>
                        {carrier ? (carrier.shortName ?? carrier.fullName) : 'без привязки к справочнику'}
                      </Text>
                      {carrier?.inn && (
                        <Text size="xs" c="dimmed">
                          ИНН {carrier.inn}
                        </Text>
                      )}
                    </div>
                  </Group>
                  {carrier && (
                    <Group gap={4} wrap="nowrap">
                      <Button
                        size="xs"
                        variant="default"
                        leftSection={<IconPlus size={14} />}
                        onClick={() => openCreate('ORDER', undefined, carrier)}
                      >
                        Поручение
                      </Button>
                      <Button
                        size="xs"
                        variant="default"
                        leftSection={<IconPlus size={14} />}
                        onClick={() => openCreate('ETRN', undefined, carrier)}
                      >
                        ЭТрН
                      </Button>
                      <Button
                        size="xs"
                        variant="default"
                        color="grape"
                        leftSection={<IconPlus size={14} />}
                        onClick={() => openCreate('ETRN', undefined, carrier, undefined, true)}
                      >
                        ЭЗЗ
                      </Button>
                    </Group>
                  )}
                </Group>
                {partyLegs.length > 0 && (
                  <Stack gap={4} mt="xs">
                    {partyLegs.map((leg) => (
                      <Group key={leg.id} justify="space-between" wrap="nowrap">
                        <Group gap="xs" wrap="nowrap">
                          <Badge
                            size="sm"
                            color={leg.kind === 'ORDER' ? 'blue' : 'grape'}
                            variant="light"
                          >
                            {KIND_LABEL[leg.kind]}
                          </Badge>
                          <Text size="sm">{leg.number}</Text>
                          <Badge size="sm" variant="outline" color="gray">
                            {DELIVERY_LEG_STATUS_LABEL[leg.status] ?? leg.status}
                          </Badge>
                          <LegEdoBadge leg={leg} />
                          <Text size="xs" c="dimmed">
                            {leg.cargo.name}
                            {legRoute(leg) ? ` · ${legRoute(leg)}` : ''}
                          </Text>
                          {formatLegPrice(leg) && (
                            <Badge size="sm" variant="light" color="green">
                              {formatLegPrice(leg)}
                            </Badge>
                          )}
                        </Group>
                        <Group gap={2} wrap="nowrap">
                          <Button
                            size="compact-xs"
                            variant="light"
                            onClick={() => onLegPdf(leg)}
                            loading={busy}
                          >
                            PDF
                          </Button>
                          <Button
                            size="compact-xs"
                            variant="light"
                            leftSection={<IconFileCode size={12} />}
                            onClick={() => onXml(leg)}
                            loading={busy}
                          >
                            XML
                          </Button>
                          <Button
                            size="compact-xs"
                            variant="subtle"
                            leftSection={<IconEdit size={12} />}
                            onClick={() => openEdit(leg)}
                          >
                            Изменить
                          </Button>
                          <Button
                            size="compact-xs"
                            variant="subtle"
                            color="red"
                            leftSection={<IconTrash size={12} />}
                            onClick={() => onDelete(leg)}
                          >
                            Удалить
                          </Button>
                        </Group>
                      </Group>
                    ))}
                  </Stack>
                )}
              </Card>
            );
          })}
        </Stack>
      )}

      {otherLegs.length === 0 ? (
        source.kind !== 'shipment' && (
          <Text c="dimmed" size="sm">
            Документов пока нет.
          </Text>
        )
      ) : (
        <>
          {source.kind === 'shipment' && (
            <Text size="sm" fw={500} mb={4}>
              Прочие документы (исполнитель не указан)
            </Text>
          )}
        <Table striped>
          <Table.Thead>
            <Table.Tr>
              <Table.Th>Документ</Table.Th>
              <Table.Th>Груз</Table.Th>
              <Table.Th>Маршрут</Table.Th>
              <Table.Th>Стоимость</Table.Th>
              <Table.Th style={{ width: 230 }} />
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {legs.map((leg) => (
              <Table.Tr key={leg.id}>
                <Table.Td>
                  <Badge color={leg.kind === 'ORDER' ? 'blue' : 'grape'} variant="light">
                    {KIND_LABEL[leg.kind]}
                  </Badge>
                  <Text size="xs">{leg.number}</Text>
                  <Badge size="xs" variant="outline" color="gray">
                    {DELIVERY_LEG_STATUS_LABEL[leg.status] ?? leg.status}
                  </Badge>
                  <LegEdoBadge leg={leg} />
                </Table.Td>
                <Table.Td>
                  <Text size="sm">{leg.cargo.name}</Text>
                  {leg.cargo.containers.length > 0 && (
                    <Text size="xs" c="dimmed">
                      {leg.cargo.containers.join(', ')}
                    </Text>
                  )}
                </Table.Td>
                <Table.Td>
                  <Text size="sm">{legRoute(leg) || leg.deliveryAddress}</Text>
                </Table.Td>
                <Table.Td>
                  <Text size="sm">{formatLegPrice(leg) ?? '—'}</Text>
                </Table.Td>
                <Table.Td>
                  <Group gap={4} wrap="nowrap">
                    <Button
                      size="xs"
                      variant="light"
                      onClick={() => onLegPdf(leg)}
                      loading={busy}
                    >
                      PDF
                    </Button>
                    <Button
                      size="xs"
                      variant="light"
                      leftSection={<IconFileCode size={14} />}
                      onClick={() => onXml(leg)}
                      loading={busy}
                    >
                      XML
                    </Button>
                    <Button
                      size="xs"
                      variant="subtle"
                      leftSection={<IconEdit size={14} />}
                      onClick={() => openEdit(leg)}
                    >
                      Изменить
                    </Button>
                    <Button
                      size="xs"
                      variant="subtle"
                      color="red"
                      leftSection={<IconTrash size={14} />}
                      onClick={() => onDelete(leg)}
                    >
                      Удалить
                    </Button>
                  </Group>
                </Table.Td>
              </Table.Tr>
            ))}
            </Table.Tbody>
          </Table>
        </>
      )}

      <Modal
        opened={formKind !== null}
        onClose={attemptClose}
        title={
          formKind ? (
            <Group gap="sm" wrap="nowrap" style={{ width: '100%' }} justify="space-between">
              <Text fw={600}>
                {`${editingId ? 'Изменить' : 'Новый документ'} — ${docKind === 'ZAKZVGO' ? 'ЭЗЗ (заказ-заявка)' : KIND_LABEL[formKind]}${
                  source.kind === 'order'
                    ? ' (плечо маршрута)'
                    : source.kind === 'shipmentLeg'
                      ? ' (плечо перевозки)'
                      : ' (привлекаемому исполнителю)'
                }`}
              </Text>
              <Badge variant="light" color="gray">
                {DELIVERY_LEG_STATUS_LABEL[
                  (editingId && legs.find((l) => l.id === editingId)?.status) || 'CREATED'
                ]}
              </Badge>
            </Group>
          ) : (
            ''
          )
        }
        styles={{ title: { flex: 1 } }}
        size="xl"
      >
        <Stack>
          <Group grow align="flex-end">
            <TextInput
              label="Номер документа"
              description={editingId ? 'Можно изменить вручную' : 'Оставьте пустым для автонумерации'}
              placeholder={editingId ? undefined : 'авто'}
              value={numberValue}
              onChange={(e) => setNumberValue(e.currentTarget.value)}
            />
            <Select
              label="Статус"
              data={Object.entries(DELIVERY_LEG_STATUS_LABEL).map(([value, label]) => ({
                value,
                label,
              }))}
              value={status}
              onChange={(v) => v && setStatus(v as DeliveryLegStatus)}
            />
          </Group>

          {/* Стоимость перевозки привлекаемому исполнителю — вносит менеджер. Может
              меняться в ходе перевозки (титул 5 — согласование новой стоимости). */}
          <Group grow align="flex-end">
            <NumberInput
              label="Стоимость перевозки"
              description="Что платим привлекаемому исполнителю"
              placeholder="0.00"
              min={0}
              decimalScale={2}
              thousandSeparator=" "
              value={price}
              onChange={(v) => setPrice(v === '' ? '' : Number(v))}
            />
            <Select
              label="Ставка НДС"
              data={VAT_RATE_OPTIONS}
              value={priceVatRate}
              onChange={setPriceVatRate}
            />
            <Select
              label="Валюта"
              data={['RUB', 'USD', 'EUR', 'CNY']}
              value={priceCurrency}
              onChange={(v) => v && setPriceCurrency(v)}
            />
          </Group>

          <CollapsibleParty
            label={isOrderSource ? 'Заказчик перевозки (клиент)' : 'Заказчик (наш экспедитор)'}
            value={customer}
            onChange={setCustomer}
            withEdo={formKind === 'ETRN'}
            problems={secProblems('customer')}
          >
            {formKind === 'ETRN' && (
              <ContractEtrnFields value={etrnDetails} onChange={setEtrnDetails} />
            )}
          </CollapsibleParty>
          <CollapsibleParty
            label={isOrderSource ? 'Экспедитор' : 'Исполнитель (Перевозчик)'}
            value={expeditor}
            onChange={setExpeditor}
            withEdo={formKind === 'ETRN'}
            problems={secProblems('carrier')}
          />
          <CollapsibleParty
            label="Грузоотправитель"
            value={shipper}
            onChange={setShipper}
            withEdo={formKind === 'ETRN'}
            problems={secProblems('shipper')}
          />
          {formKind === 'ETRN' && (
            <Checkbox
              label="Грузоотправитель является экспедитором (ГОЭксп)"
              checked={shipperIsExpeditor}
              onChange={(e) => setShipperIsExpeditor(e.currentTarget.checked)}
            />
          )}
          <CollapsibleParty
            label="Грузополучатель"
            value={consignee}
            onChange={setConsignee}
            withEdo={formKind === 'ETRN'}
            problems={secProblems('consignee')}
          />
          <CollapseCard
            title="Маршрут"
            summary={`${pickupAddress || '—'} → ${deliveryAddress || '—'}`}
            problems={secProblems('route')}
          >
            <Stack>
              <Group grow>
                <TextInput
                  label="Пункт отправления (откуда)"
                  placeholder={isOrderSource ? 'адрес, станция, порт' : 'терминал, склад, адрес'}
                  withAsterisk
                  value={pickupAddress}
                  onChange={(e) => setPickupAddress(e.currentTarget.value)}
                  error={!pickupAddress.trim() ? 'обязательно' : undefined}
                />
                <TextInput
                  label="Пункт доставки (куда)"
                  withAsterisk
                  value={deliveryAddress}
                  onChange={(e) => setDeliveryAddress(e.currentTarget.value)}
                  error={!deliveryAddress.trim() ? 'обязательно' : undefined}
                />
              </Group>
              {formKind === 'ETRN' && (
                <Group grow align="flex-start">
                  <div>
                    <Text size="xs" fw={500} mb={2}>
                      Отправление
                    </Text>
                    <AddressBreakdownFields value={pickupParts} onChange={setPickupParts} />
                  </div>
                  <div>
                    <Text size="xs" fw={500} mb={2}>
                      Доставка
                    </Text>
                    <AddressBreakdownFields
                      value={deliveryParts}
                      onChange={setDeliveryParts}
                      withDeliveryExtras
                    />
                  </div>
                </Group>
              )}
            </Stack>
          </CollapseCard>

          <CollapseCard
            title="Груз"
            summary={[
              cargo.name || 'без наименования',
              cargo.weightKg ? `${cargo.weightKg} кг` : null,
              cargo.units ? `${cargo.units} мест` : null,
            ]
              .filter(Boolean)
              .join(', ')}
            problems={secProblems('cargo')}
          >
            <Stack>
              {/* Контейнер для ЭТрН выбирается до открытия формы (режим «один
                  контейнер»); здесь его груз уже подставлен. Селект груза из
                  поручения в этом режиме не показываем. */}
              {!(formKind === 'ETRN' && oneContainerPerDoc && containerUnits.length > 0) &&
                cargoOptionData.length > 1 && (
                  <Select
                    label="Выбрать груз из базового поручения"
                    data={cargoOptionData}
                    value={null}
                    placeholder="подставить груз/контейнеры"
                    onChange={(v) => v !== null && setCargoFrom(cargoOptions[Number(v)])}
                  />
                )}
              <Group grow>
                <TextInput
                  label="Наименование груза"
                  value={cargo.name}
                  onChange={(e) => setCargo({ ...cargo, name: e.currentTarget.value })}
                />
                <NumberInput
                  label="Вес брутто, кг"
                  value={cargo.weightKg ?? ''}
                  onChange={(v) => setCargo({ ...cargo, weightKg: v === '' ? undefined : Number(v) })}
                  min={0}
                />
                <NumberInput
                  label="Вес нетто, кг"
                  value={cargo.weightNetKg ?? ''}
                  onChange={(v) =>
                    setCargo({ ...cargo, weightNetKg: v === '' ? undefined : Number(v) })
                  }
                  min={0}
                />
                <NumberInput
                  label="Мест"
                  value={cargo.units ?? ''}
                  onChange={(v) => setCargo({ ...cargo, units: v === '' ? undefined : Number(v) })}
                  min={0}
                />
              </Group>
              {formKind === 'ETRN' && (
                <Select
                  label="Метод определения массы"
                  description="Обязательно для ЭТрН (МетОпрМасс)"
                  placeholder="выберите"
                  data={MASS_METHOD_OPTIONS}
                  value={etrnDetails.loading?.massMethod ?? null}
                  onChange={(v) =>
                    setEtrnDetails({
                      ...etrnDetails,
                      loading: { ...(etrnDetails.loading ?? {}), massMethod: v ?? undefined },
                    })
                  }
                  clearable
                />
              )}
              <Group grow>
                <TextInput
                  label="Способ упаковки"
                  description="Описание — ЭТрН: СпУпак. Подставится из вида упаковки, можно уточнить"
                  value={cargo.packaging ?? ''}
                  onChange={(e) => setCargo({ ...cargo, packaging: e.currentTarget.value })}
                />
                <CodeSelect
                  fetchOptions={(q) => referenceDataApi.classifierSearch('packaging', q)}
                  fetchByCode={(c) => referenceDataApi.classifierByCode('packaging', c)}
                  label="Вид упаковки (тары)"
                  value={cargo.packagingCode ?? ''}
                  onChange={(code) => {
                    if (!code) {
                      setCargo((prev) => ({ ...prev, packagingCode: '' }));
                      return;
                    }
                    referenceDataApi.classifierByCode('packaging', code).then((r) =>
                      setCargo((prev) => ({
                        ...prev,
                        packagingCode: code,
                        packaging: prev.packaging?.trim() ? prev.packaging : (r?.name ?? ''),
                      })),
                    );
                  }}
                />
              </Group>
              <Group grow>
                <TextInput
                  label="ТН ВЭД"
                  value={cargo.tnVed ?? ''}
                  onChange={(e) => setCargo({ ...cargo, tnVed: e.currentTarget.value })}
                />
                <TextInput
                  label="Маркировка"
                  value={cargo.marking ?? ''}
                  onChange={(e) => setCargo({ ...cargo, marking: e.currentTarget.value })}
                />
              </Group>
              {cargo.containers.length > 0 && (
                <Text size="xs" c="dimmed">
                  Контейнеры: {cargo.containers.join(', ')}
                </Text>
              )}
              {/* В груз — габариты, опасный груз, пломбы и указания (для ЭТрН). */}
              {formKind === 'ETRN' && (
                <CargoEtrnFields value={etrnDetails} onChange={setEtrnDetails} />
              )}
            </Stack>
          </CollapseCard>

          {formKind === 'ETRN' && (
            <>
              <CollapseCard
                title="Транспортное средство"
                summary={[etrnDetails.vehicle?.kind, etrnDetails.vehicle?.regNumber || vehicleRegNo]
                  .filter(Boolean)
                  .join(' ') || 'не указано'}
                problems={secProblems('transport')}
              >
                <TransportEtrnFields
                  value={etrnDetails}
                  onChange={setEtrnDetails}
                  driverName={driverName}
                  setDriverName={setDriverName}
                  driverPhone={driverPhone}
                  setDriverPhone={setDriverPhone}
                />
              </CollapseCard>

              <CollapseCard title="Погрузка" problems={secProblems('loading')}>
                <LoadingContractEtrnFields value={etrnDetails} onChange={setEtrnDetails} />
              </CollapseCard>

              <CollapseCard
                title="Отметки (блок 11)"
                summary={
                  etrnDetails.marks?.length
                    ? `${etrnDetails.marks.length} отмет.`
                    : 'нет'
                }
              >
                <MarksEtrnFields value={etrnDetails} onChange={setEtrnDetails} />
              </CollapseCard>
            </>
          )}

          {checkProblems !== null &&
            (checkProblems.length === 0 ? (
              <Alert color="green">Все обязательные поля ЭТрН заполнены.</Alert>
            ) : (
              <Alert color="red" title={`Не заполнено обязательных полей: ${checkProblems.length}`}>
                Незаполненные блоки подсвечены и раскрыты выше.
              </Alert>
            ))}

          {edoValidation && (
            <Alert
              color={edoValidation.errors.length > 0 ? 'red' : 'yellow'}
              withCloseButton
              onClose={() => {
                setEdoValidation(null);
                setEdoBlocked(false);
              }}
              title={
                edoValidation.errors.length > 0
                  ? `ЭДО.МИГ24: ошибки валидации XML (${edoValidation.errors.length})`
                  : `ЭДО.МИГ24: замечания (${edoValidation.warnings.length})`
              }
            >
              <Stack gap={2}>
                {edoValidation.errors.map((m, i) => (
                  <Text key={`e${i}`} size="sm" c="red">
                    • {m}
                  </Text>
                ))}
                {edoValidation.warnings.map((m, i) => (
                  <Text key={`w${i}`} size="sm" c="orange">
                    • {m}
                  </Text>
                ))}
                {edoBlocked && (
                  <Group gap="xs" mt="xs">
                    <Text size="sm" c="dimmed">
                      Перевозка не создана из-за ошибок. Можно создать её всё равно —
                      с отложенной проверкой.
                    </Text>
                    <Button
                      size="xs"
                      color="red"
                      variant="light"
                      loading={busy}
                      onClick={onSignEdoForce}
                    >
                      Всё равно создать с ошибками
                    </Button>
                  </Group>
                )}
              </Stack>
            </Alert>
          )}

          {editingId && (
            <LegDocumentsCard legId={editingId} reloadKey={docsReloadKey} onError={setError} />
          )}

          {/* Статус и титулы из ЭДО.МИГ24 — когда плечо уже отправлено (есть ссылка). */}
          {editingId && edoSignPageUrl && (
            <EdoStateCard legId={editingId} onError={setError} />
          )}

          <Group justify="space-between">
            {formKind ? (
              <Group>
                {(formKind === 'ETRN' || formKind === 'ORDER') && docKind !== 'ZAKZVGO' && (
                  <Button variant="default" color="orange" onClick={runCheck}>
                    Проверить заполнение
                  </Button>
                )}
                {docKind === 'ZAKZVGO' ? (
                  <Button color="grape" onClick={onIssueZakzvForm} loading={busy}>
                    Выпустить ЭЗЗ
                  </Button>
                ) : (
                  <Button color="teal" onClick={onSignEdo} loading={busy}>
                    {formKind === 'ORDER'
                      ? 'Подписать поручение в ЭДО.МИГ24'
                      : 'Подписать в ЭДО.МИГ24'}
                  </Button>
                )}
                {formKind === 'ORDER' && editingId && (
                  <Button variant="light" color="red" onClick={onRevokePoruchenie} loading={busy}>
                    Отозвать поручение
                  </Button>
                )}
                {edoSignPageUrl && (
                  <Button
                    variant="light"
                    color="teal"
                    leftSection={<IconExternalLink size={16} />}
                    onClick={() => {
                      const w = window.open(edoSignPageUrl, '_blank', 'noopener,noreferrer');
                      if (!w) setSignPageUrl(edoSignPageUrl);
                    }}
                  >
                    {formKind === 'ORDER'
                      ? 'Открыть поручение в ЭДО.МИГ24'
                      : 'Открыть перевозку в ЭДО.МИГ24'}
                  </Button>
                )}
              </Group>
            ) : (
              <div />
            )}
            <Group>
              <Button variant="default" onClick={attemptClose}>
                Закрыть
              </Button>
              <Button onClick={onSave} loading={busy}>
                {editingId ? 'Сохранить' : 'Создать'}
              </Button>
            </Group>
          </Group>
        </Stack>
      </Modal>

      {/* Закрытие с несохранёнными изменениями */}
      <Modal
        opened={closeAsk}
        onClose={() => setCloseAsk(false)}
        title="Закрыть форму?"
        size="md"
        zIndex={2000}
      >
        <Stack>
          <Text size="sm">
            В форме есть несохранённые изменения. Сохранить их перед закрытием?
          </Text>
          <Group justify="flex-end">
            <Button variant="subtle" onClick={() => setCloseAsk(false)}>
              Отмена
            </Button>
            <Button
              variant="default"
              color="red"
              onClick={() => {
                setCloseAsk(false);
                setFormKind(null);
              }}
            >
              Закрыть без сохранения
            </Button>
            <Button
              loading={busy}
              onClick={async () => {
                const ok = await onSave();
                if (ok) {
                  setCloseAsk(false);
                  setFormKind(null);
                }
              }}
            >
              Сохранить и закрыть
            </Button>
          </Group>
        </Stack>
      </Modal>

      {/* Выбор контейнера для ЭТрН (режим «один контейнер на документ») */}
      <Modal
        opened={!!containerPick}
        onClose={() => setContainerPick(null)}
        title="Выберите контейнер для ЭТрН"
        size="md"
      >
        <Stack>
          <Text size="sm" c="dimmed">
            ЭТрН оформляется на один контейнер поручения. Данные контейнера
            перенесутся в документ, а номер ЭТрН = номер контейнера без букв.
          </Text>
          {containerPick?.defaults.containerUnits.map((u) => (
            <Button
              key={u.id}
              variant="default"
              justify="space-between"
              rightSection={
                <Text size="xs" c="dimmed">
                  {u.number ? `№ ЭТрН: ${containerDigits(u.number)}` : 'без номера'}
                </Text>
              }
              onClick={() => {
                const ctx = containerPick;
                setContainerPick(null);
                applyDefaults('ETRN', ctx.defaults, ctx.segment, ctx.performer, u.id);
              }}
            >
              {u.label}
            </Button>
          ))}
        </Stack>
      </Modal>

      {/* Ссылка на страницу подписания ЭДО.МИГ24 — показываем, если попап был
          заблокирован (обычно открывается сразу в новой вкладке). */}
      <Modal
        opened={!!signPageUrl}
        onClose={() => setSignPageUrl(null)}
        title="Подписание в ЭДО.МИГ24"
        size="md"
        zIndex={2100}
      >
        <Stack>
          <Text size="sm">
            Перевозка создана в ЭДО.МИГ24. Откройте страницу подписания титула 1 —
            там выбирается способ подписи. Статус документа обновится автоматически
            после обработки.
          </Text>
          {signPageUrl && (
            <Button
              component="a"
              href={signPageUrl}
              target="_blank"
              rel="noreferrer"
              leftSection={<IconFileCode size={16} />}
              onClick={() => setSignPageUrl(null)}
            >
              Открыть страницу подписания (новая вкладка)
            </Button>
          )}
        </Stack>
      </Modal>
    </Card>
  );
}
